import { AddResultStatus, Color, FileType, InputType, TwoDimImportResultType, } from "Qrc/lib/generated/enum";
import { isStringIndexedInterface, isTwoDimImportResultEngravingInvalid, isTwoDimImportResultPartInvalid, isTwoDimImportResultSuccess, } from "Qrc/lib/generated/typeguard";
import { addToGraph, } from "Qrc/lib/graph_manipulator";
import { getAllSubJoinings, } from "Qrc/lib/graph_utils";
import { assert, assertDebug, getKeysOfObject, isArray, isNumber, isObject, isString, } from "Qrc/lib/utils";
export function isInputEntry(arg) {
    const map = {
        importId: isString,
        data: isString,
        name: (arg) => arg === undefined || isString(arg),
        thickness: (arg) => arg === undefined || isNumber(arg),
    };
    return isStringIndexedInterface(arg) && getKeysOfObject(map)
        .every(key => map[key](arg[key]));
}
export function isTwoDimInput(arg) {
    const map = {
        layered: (arg) => isObject(arg),
        cuttingLds: (arg) => isArray(arg, isNumber),
        engravingLds: (arg) => isArray(arg, isNumber),
        importId: isString,
        name: (arg) => isString(arg),
        thickness: (arg) => isNumber(arg),
    };
    return isStringIndexedInterface(arg) && getKeysOfObject(map)
        .every(key => map[key](arg[key]));
}
function layerColor(layer) {
    const colorMap = [
        Color.closedContour,
        Color.closedContour,
        Color.yellow,
        Color.green,
        Color.cyan,
        Color.blue,
        Color.magenta,
        Color.white,
        Color.red,
    ];
    return (layer.number >= 0 && layer.number < colorMap.length) ? colorMap[layer.number] : undefined;
}
function layerToSvg(layered, layer, viewPort) {
    const lc = layerColor(layer);
    const color = lc === undefined || lc === Color.white ? Color.closedContour : lc;
    const style = {
        strokeWidth: 2,
        strokeColor: color,
    };
    const scene = wsi4.geo.util.layersToScene(layered, [layer.descriptor], style);
    return wsi4.util.arrayBufferToString(wsi4.geo.util.renderScene(scene, FileType.svg, {
        viewPort: viewPort,
    }));
}
function computeLayerViewData(layered) {
    const viewPort = wsi4.geo.util.boundingBox2d(layered);
    return wsi4.geo.util.layers(layered)
        .map(layer => ({
        descriptor: layer.descriptor,
        name: layer.name,
        svg: wsi4.geo.util.isLayerEmpty(layered, layer.descriptor) ? "" : layerToSvg(layered, layer, viewPort),
        color: layerColor(layer),
    }));
}
function preProcessLayered(twoDimInput) {
    const tolerances = [
        0.001,
        0.002,
        0.005,
        0.01,
        0.02,
        0.05,
        0.1,
    ];
    for (const tol of tolerances) {
        const result = wsi4.cam.util.twoDimRepFromLayered(twoDimInput.layered, twoDimInput.cuttingLds, twoDimInput.engravingLds, tol);
        if (result.type === TwoDimImportResultType.success) {
            const resultContent = result.content;
            assert(isTwoDimImportResultSuccess(resultContent));
            const content = {
                twoDimRep: resultContent.twoDimRep,
                thickness: twoDimInput.thickness,
                name: twoDimInput.name,
                id: twoDimInput.importId,
            };
            return {
                tag: "ValidInput",
                importId: twoDimInput.importId,
                input: {
                    type: InputType.twoDimRep,
                    content: content,
                },
            };
        }
        else {
            const resultContent = result.content;
            assert(isTwoDimImportResultPartInvalid(resultContent) || isTwoDimImportResultEngravingInvalid(resultContent));
        }
    }
    return {
        tag: "InvalidLayered",
        importId: twoDimInput.importId,
        name: twoDimInput.name,
        layered: twoDimInput.layered,
        cuttingLds: twoDimInput.cuttingLds,
        engravingLds: twoDimInput.engravingLds,
        layerViewDatas: computeLayerViewData(twoDimInput.layered),
    };
}
function processInputPhase0(inputEntry) {
    var _a, _b;
    const buffer = wsi4.util.fromBase64(wsi4.util.stringToArrayBuffer(inputEntry.data));
    const type = wsi4.classifier.classify(buffer);
    switch (type) {
        case InputType.twoDimRep:
        case InputType.undefined: return ({
            tag: "InvalidInput",
            importId: inputEntry.importId,
        });
        case InputType.assembly: {
            const content = {
                id: inputEntry.importId,
                data: buffer,
            };
            return {
                tag: "ValidInput",
                importId: inputEntry.importId,
                input: {
                    type: type,
                    content: content,
                },
            };
        }
        case InputType.documentGraph: {
            const content = {
                id: inputEntry.importId,
                data: buffer,
            };
            return {
                tag: "ValidInput",
                importId: inputEntry.importId,
                input: {
                    type: type,
                    content: content,
                },
            };
        }
        case InputType.layered: {
            const layered = wsi4.geo.util.createLayered(buffer);
            if (layered === undefined) {
                return {
                    tag: "InvalidInput",
                    importId: inputEntry.importId,
                };
            }
            else {
                const cuttingLds = wsi4.geo.util.layers(layered)
                    .map(layer => layer.descriptor);
                const engravingLds = [];
                return preProcessLayered({
                    layered: layered,
                    cuttingLds: cuttingLds,
                    engravingLds: engravingLds,
                    thickness: (_a = inputEntry.thickness) !== null && _a !== void 0 ? _a : 1,
                    name: (_b = inputEntry.name) !== null && _b !== void 0 ? _b : "",
                    importId: inputEntry.importId,
                });
            }
        }
    }
}
function flattenGraph() {
    const subjoinings = getAllSubJoinings();
    if (subjoinings.length === 0) {
        return true;
    }
    return wsi4.graphManipulator.deleteNodes(subjoinings);
}
export function privateFlattenGraph() {
    return flattenGraph();
}
function processInputPhase1(resultsPhase0) {
    return addToGraph(resultsPhase0.filter((obj) => obj.tag === "ValidInput")
        .map(obj => obj.input))
        .map(result => ({
        importId: result.id,
        success: result.status === AddResultStatus.success,
        rootIds: result.vertices.map(vertex => wsi4.node.rootId(vertex)),
    }));
}
function processInputPhase2(resultsPhase0, resultsPhase1) {
    const group0 = resultsPhase0.filter((obj) => obj.tag !== "ValidInput");
    const group1 = resultsPhase1.map((obj) => {
        if (obj.success) {
            const vertices = obj.rootIds.map(rootId => {
                const vertex = wsi4.node.vertexFromRootId(rootId);
                assert(vertex !== undefined, "Expecting matching vertex");
                return vertex;
            });
            return {
                tag: "AddSuccess",
                importId: obj.importId,
                vertexKeys: vertices.map(vertex => wsi4.util.toKey(vertex)),
            };
        }
        else {
            return {
                tag: "InvalidInput",
                importId: obj.importId,
            };
        }
    });
    return [
        ...group0,
        ...group1,
    ];
}
function handlePhase0Results(resultsPhase0, flattenGraphEnabled) {
    const resultsPhase1 = processInputPhase1(resultsPhase0.filter((result) => result.tag === "ValidInput"));
    if (flattenGraphEnabled) {
        if (!flattenGraph()) {
            return [];
        }
    }
    return processInputPhase2(resultsPhase0, resultsPhase1);
}
function addDataImpl(inputs, flattenGraphEnabled) {
    const resultsPhase0 = inputs.map(input => processInputPhase0(input));
    const resultsPhase2 = handlePhase0Results(resultsPhase0, flattenGraphEnabled);
    assertDebug(() => inputs.every(input => resultsPhase2.some(obj => obj.importId === input.importId)), "Expecting corresponding result for each input entry");
    assertDebug(() => resultsPhase2.filter((obj) => obj.tag === "AddSuccess")
        .every(entry => entry.vertexKeys.every(key => wsi4.graph.vertices()
        .some(vertex => key === wsi4.util.toKey(vertex)))), "Expecting valid vertices only");
    return resultsPhase2;
}
export function addData(inputObjects) {
    return addDataImpl(inputObjects, false);
}
export function addDataAndFlattenGraph(inputObjects) {
    return addDataImpl(inputObjects, true);
}
export function addLayereds(inputs) {
    const resultsPhase0 = inputs.map(input => preProcessLayered(input));
    return handlePhase0Results(resultsPhase0, false);
}
